import { fetchAuthSession } from 'aws-amplify/auth';
import i18n from '../i18n';
import { runnerMutationCompleted } from './mutationEvents';

/**
 * Thin client for glplus-runner-api. Attaches the Runner-pool id token as a
 * Bearer credential; the RunnerHttpApi's JWT authorizer verifies it against
 * the Runner pool and runner-api re-derives capabilities from the profile's
 * lifecycle on every call. Self-contained by ruling — no shared-libs.
 */
const BASE = import.meta.env.VITE_RUNNER_API_URL as string | undefined;

export class RunnerApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /**
     * The body's `data`, when there is one. A 409 from time off carries the
     * deliveries those days would stand up — dropping it here would leave the
     * panel with nothing to show but a sentence.
     */
    public readonly data?: unknown,
  ) {
    super(message);
    this.name = 'RunnerApiError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(method: string, path: string, body?: unknown, anonymous = false): Promise<T> {
  if (!BASE) throw new RunnerApiError('VITE_RUNNER_API_URL is not configured', 0);
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(anonymous ? {} : await authHeaders()) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* empty body */
  }
  if (!res.ok) {
    const data = (payload as { data?: unknown } | null)?.data;
    const code = (data as { code?: string } | undefined)?.code;
    const msg = code === HANDBOOK_ACK_REQUIRED
      ? i18n.t('handbook.reackRequired')
      : code === INSURANCE_REQUIRED
        ? i18n.t('insurance.required')
        : (payload as { error?: string; message?: string } | null)?.error ?? (payload as { message?: string } | null)?.message ?? `Request failed (${res.status})`;
    throw new RunnerApiError(msg, res.status, data);
  }
  if (!anonymous && !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) runnerMutationCompleted();
  return (payload as { data: T }).data;
}

/** runner-api refuses new work with this code until the current handbook is acknowledged. */
export const HANDBOOK_ACK_REQUIRED = 'HANDBOOK_ACK_REQUIRED';
/** runner-api refuses new work with this code until an approved, unexpired insurance card is on file. */
export const INSURANCE_REQUIRED = 'INSURANCE_REQUIRED';

export type Lifecycle = 'applicant' | 'active' | 'paused' | 'suspended' | 'deactivated';

export interface Me {
  sub: string;
  email: string;
  runnerId: string | null;
  glpdId: string | null;
  recovery?: { purgeAt: string; canRestore: boolean } | null;
  lifecycle: Lifecycle;
  displayName: string | null;
  /** NULL until chosen; the portal fills it from this browser's choice. */
  preferredLanguage?: 'en' | 'es' | null;
  capabilities: string[];
  handbookVersionCurrent?: string;
  handbookVersionAcked?: string | null;
  /** A live Runner owes a re-acknowledgement; new work is refused until they give it. */
  handbookAckRequired?: boolean;
  phoneE164?: string | null;
  /** No phone on file; the Runner app refuses checkout until one is added. */
  phoneRequired?: boolean;
  /** No approved, unexpired insurance card; new work is refused until one is approved. */
  insuranceRequired?: boolean;
  insuranceExpiresOn?: string | null;
}

/**
 * Onboarding order (Rod, 2026-10-01): review → profile → insurance → handbook →
 * video call → background check → banking → final review. `shadow_ride` is
 * still in the server enum but is never reached.
 */
export type ApplicationStatus =
  | 'submitted' | 'in_review' | 'declined_prescreen' | 'profile_setup' | 'insurance_review'
  | 'training_ack' | 'verification_call' | 'checkr_pending' | 'checkr_consider' | 'checkr_clear'
  | 'checkr_waived' | 'declined_checkr' | 'banking_tax' | 'shadow_ride' | 'pre_golive_review'
  | 'approved' | 'live' | 'withdrawn';

export interface MyApplication {
  id: string;
  status: ApplicationStatus;
  metro: string;
  zonesInterested: string[];
  transportMode: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneE164: string | null;
  submittedAt: string;
  updatedAt: string;
  trainingAckedAt: string | null;
  handbookVersionAcked: string | null;
  kitDisclaimerAckedAt: string | null;
  withdrawnAt: string | null;
}

export interface UpdateMyApplication {
  phoneE164?: string | null;
  zonesInterested?: string[];
  transportMode?: string;
}

export type PhotoKind = 'profile' | 'vehicle';
export type PhotoStatus = 'pending' | 'live' | 'rejected' | null;
export type TransportMode = 'car' | 'suv_van' | 'bike' | 'ebike' | 'scooter' | 'on_foot';

export interface MyPhotos {
  ready: boolean;
  profile: { liveUrl: string | null; submittedUrl: string | null; status: PhotoStatus; rejectionReason: string | null } | null;
  vehicle: { url: string | null; status: PhotoStatus } | null;
  vehicleDetails: {
    make: string | null;
    model: string | null;
    color: string | null;
    transportMode: TransportMode;
  } | null;
}

export interface UpdateVehicle {
  transportMode?: TransportMode;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleColor?: string | null;
}

/**
 * Upload is a THREE step dance and all three must succeed to count:
 * ask runner-api for a presigned PUT, send the bytes straight to S3 (the
 * bytes never touch our Lambda), then tell runner-api the key is ready so it
 * enters review. A failure at step 2 or 3 leaves nothing pending.
 */
export type InsuranceStatus = 'pending' | 'approved' | 'rejected' | 'superseded';

export interface InsurancePolicy {
  id: string;
  insurer: string;
  policyNumber: string;
  /** YYYY-MM-DD, as printed on the card. */
  expiresOn: string;
  daysLeft: number;
  status: InsuranceStatus;
  submittedAt: string;
  reviewedAt: string | null;
  rejectReason: string | null;
  cardViewUrl: string | null;
}

export interface MyInsurance {
  ready: boolean;
  current: InsurancePolicy | null;
  currentValid: boolean;
  pending: InsurancePolicy | null;
  lastSentBack: InsurancePolicy | null;
}

export interface SubmitInsurance {
  insurer: string;
  policyNumber: string;
  expiresOn: string;
}

export const INSURANCE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] as const;

/** Same three steps as a photo: presigned PUT, bytes straight to S3, then the details. */
async function submitInsurance(details: SubmitInsurance, file: File): Promise<{ policy: InsurancePolicy }> {
  const { uploadUrl, key, maxBytes } = await request<{ uploadUrl: string; key: string; maxBytes: number }>(
    'POST', '/runner/me/insurance/upload-url', { contentType: file.type },
  );
  if (file.size > maxBytes) {
    throw new RunnerApiError(i18n.t('insurance.tooLarge', { size: Math.round(file.size / 1024 / 1024), max: Math.round(maxBytes / 1024 / 1024) }), 400);
  }
  const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
  if (!put.ok) throw new RunnerApiError(i18n.t('insurance.uploadFailed'), put.status);
  return request<{ policy: InsurancePolicy }>('POST', '/runner/me/insurance', { ...details, cardKey: key });
}

export interface VerificationCall {
  id: string;
  status: string;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  joinUrl: string | null;
  interviewerName: string | null;
  completedAt: string | null;
}

export interface MyVerificationCall {
  applicationStatus: ApplicationStatus;
  call: VerificationCall | null;
  /** Non-null only while the application is at verification_call and the call is not completed. */
  bookingUrl: string | null;
}

async function uploadPhoto(kind: PhotoKind, file: File): Promise<void> {
  const { uploadUrl, key, maxBytes } = await request<{ uploadUrl: string; key: string; maxBytes: number }>(
    'POST', '/runner/me/photo/upload-url', { kind, contentType: file.type },
  );
  if (file.size > maxBytes) {
    throw new RunnerApiError(`That photo is ${Math.round(file.size / 1024 / 1024)} MB. Keep it under ${Math.round(maxBytes / 1024 / 1024)} MB.`, 400);
  }
  const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
  if (!put.ok) throw new RunnerApiError('That upload did not go through. Try again.', put.status);
  await request<{ kind: string; status: string }>('POST', '/runner/me/photo', { kind, key });
}


// ─── §6 availability ─────────────────────────────────────────────────────────
/** 0 = Sunday … 6 = Saturday, matching Postgres EXTRACT(DOW). */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * The window a Runner OFFERS is the customer-facing DELIVERY window. The pickup
 * window is derived server-side (delivery − vendor prep − cutoff) and is never
 * sent or stored — the API rejects a pickup field rather than dropping it.
 */
export interface AvailabilityWindow {
  id: string;
  dayOfWeek: DayOfWeek;
  deliveryWindowStart: string;   // HH:MM, 24h
  deliveryWindowEnd: string;     // HH:MM, 24h
  zone: string;
  /** Deliveries per week — NOT customers. One customer can take several. */
  capacityDeliveries: number;
  effectiveFrom: string;         // YYYY-MM-DD
  effectiveTo: string | null;    // null = open-ended
}

export type CreateAvailability = Omit<AvailabilityWindow, 'id' | 'effectiveFrom' | 'effectiveTo'> &
  Partial<Pick<AvailabilityWindow, 'effectiveFrom' | 'effectiveTo'>>;

export type UpdateAvailability = Partial<Omit<AvailabilityWindow, 'id'>>;


// ─── §6 time off ─────────────────────────────────────────────────────────────
export type TimeOffStatus = 'requested' | 'approved' | 'denied' | 'cancelled';

export interface TimeOff {
  id: string;
  startsOn: string;              // YYYY-MM-DD
  endsOn: string;                // YYYY-MM-DD
  reason: string | null;
  status: TimeOffStatus;
  /** Ops assigns coverage; a Runner cannot set it. Read-only here. */
  backupRunnerId: string | null;
  customerNoticeSentAt: string | null;
  /** Days between the request being made and the absence starting. Negative
   *  when it had already started — reporting an illness underway. */
  noticeDays: number;
  /** Requested with less than the customer-notice target. A flag for ops,
   *  never a reason the request was refused. */
  shortNotice: boolean;
}

export interface CreateTimeOff {
  startsOn: string;
  endsOn: string;
  reason?: string;
  /** Send once the Runner has SEEN what those days would stand up. */
  acknowledgeDeliveries?: boolean;
}

/** One run inside the requested days that already has orders on it. */
export interface AffectedDelivery {
  runId: string;
  runDate: string;
  deliveryWindowStart: string;
  deliveryWindowEnd: string;
  zone: string | null;
  orderCount: number;
  noticeHours: number;
  /** Inside 72 hours — this one goes on the Runner's record. */
  shortNotice: boolean;
}

/** The 409 body when days already carry deliveries. */
export interface DeliveryConflict {
  affected: AffectedDelivery[];
  totalDeliveries: number;
  shortNoticeCount: number;
  acknowledgeWith: 'acknowledgeDeliveries';
}

export function asDeliveryConflict(e: unknown): DeliveryConflict | null {
  if (!(e instanceof RunnerApiError) || e.status !== 409) return null;
  const d = e.data as DeliveryConflict | undefined;
  return d && Array.isArray(d.affected) ? d : null;
}

/** Days in a range that already carry deliveries, for marking the picker. */
/**
 * A run another Runner could not keep.
 *
 * 🔒 Accepting one NEVER counts against this Runner's weekly deliveries or
 * household cap (Rod, 2026-09-12) — which is why it is worth showing to a
 * Runner who is already at their ceiling.
 */
export interface CoverOffer {
  id: string;
  zone: string;
  metro: string;
  runDate: string;
  deliveryWindowStart: string;
  deliveryWindowEnd: string;
  orderCount: number;
  expiresAt: string;
  status: string;
}

export interface TimeOffCalendar {
  from: string;
  to: string;
  days: { date: string; deliveries: number }[];
}


// ─── §5 stage 7 — banking (Stripe Connect Express) ───────────────────────────
export interface ConnectStatus {
  accountId: string;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  /** Stripe's own outstanding-requirement keys, e.g. 'external_account'. */
  requirementsDue: string[];
  disabledReason: string | null;
  /** 🔑 Computed server-side. Never re-derive it in the UI — one definition. */
  payable: boolean;
}

export interface BankingState {
  started: boolean;
  payable: boolean;
  status: ConnectStatus | null;
}


// ─── §13 step 9a — pickup runs ───────────────────────────────────────────────
/** Mirrors the Postgres enum pickup_run_status. */
export type RunStatus =
  | 'open' | 'locked' | 'in_prep' | 'pickup_in_progress'
  | 'out_for_delivery' | 'completed' | 'settled' | 'cancelled';

export interface RunStop {
  id: string;
  sequence: number;
  stopKind: 'chain_store' | 'local_vendor';
  displayName: string;
  banner: string | null;
  address: unknown;
  fulfillmentMode: 'runner_picks' | 'vendor_prepped';
  prepStatus: 'received' | 'prepping' | 'ready' | 'picked_up' | null;
  shopTimeBudgetMinutes: number | null;
  arrivedAt: string | null;
  pickedUpAt: string | null;
  notes: string | null;
}

/** One customer's order on a run, in drop sequence. Present on the detail read only. */
export interface RunOrder {
  id: string;
  orderId: string;
  glpo?: string | null;
  customerDisplayName?: string | null;
  /** The customer's public id — the only identity runner-api holds. */
  customerGlpUserId: string;
  fulfillmentStatus: string;
  deliverySequence: number | null;
  deliveredAt: string | null;
  confirmedAt: string | null;
  exceptionCode: string | null;
  /** Nobody home (2026-09-27): when the return attempt was set, when it ended undelivered, why, and when it was donated. */
  retryPendingAt?: string | null;
  undeliveredAt?: string | null;
  undeliveredReason?: 'nobody_home' | 'safety' | null;
  donatedAt?: string | null;
  /** The 8 PM card hold (2026-09-30): at_risk = the customer's card was declined. */
  paymentHold?: 'ok' | 'at_risk' | null;
  cardFixBy?: string | null;
}

/**
 * 🛑 No card, receipt or seal fields. The API deliberately does not serialize
 * them — they are settlement inputs (9b, gated on Stripe Issuing), and a client
 * type that carried them would invite a screen to compute pay from the run
 * instead of from the ledger that owns it.
 */
export interface PickupRun {
  id: string;
  metro: string;
  zone: string;
  /** YYYY-MM-DD in the METRO's timezone, not UTC. */
  runDate: string;
  deliveryWindowStart: string;  // HH:MM
  deliveryWindowEnd: string;    // HH:MM
  status: RunStatus;
  cutoffAt: string | null;
  orderCount: number;
  startedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  archivedAt?: string | null;
  stops?: RunStop[];
  orders?: RunOrder[];
}

// ─── §14 step 10 — earnings (the ledger's READ side) ─────────────────────────
/** Mirrors the Postgres enum runner_settlement_status. */
export type SettlementStatus = 'pending' | 'paid' | 'failed' | 'void';

/**
 * One weekly statement: earnings − lane pass ± adjustments = net. Written by
 * runner-api at settlement; the lane pass decision (grace, time off, the $60
 * cap) lives ONLY here. Nothing on the portal recomputes it.
 */
export interface Settlement {
  id: string;
  periodStart: string;        // YYYY-MM-DD
  periodEnd: string;          // YYYY-MM-DD
  deliveriesCount: number;
  feesCents: number;
  tipsCents: number;
  lanePassCents: number;      // a deduction, stored positive
  adjustmentsCents: number;   // signed
  netCents: number;
  status: SettlementStatus;
  paidAt: string | null;
}

/** Pay items not yet on a statement — the week in flight. No lane pass here on
 *  purpose: it is decided at settlement, not accrued per delivery. */
export interface UnsettledEarnings {
  deliveries: number;
  feesCents: number;
  tipsCents: number;
  /** Goodwill / make-goods, e.g. the store-closed credit. */
  compCents: number;
}

export interface EarningsResponse {
  settlements: Settlement[];
  unsettled: UnsettledEarnings;
}


// ─── §13 — customer relationships, and the k-anonymous prospect board ────────
/** Mirrors runner-api matches/transitions.ts. `ended` is final. */
export type MatchStatus = 'requested' | 'accepted' | 'active' | 'paused' | 'ended';
export type MatchInitiator = 'customer' | 'runner';

/**
 * Display names arrive through the owned relationship's consumer-side bridge.
 * No address or contact details are included in this response.
 */
export interface CustomerMatch {
  id: string;
  customerGlpUserId: string;
  customerDisplayName?: string | null;
  zone: string;
  status: MatchStatus;
  initiatedBy: MatchInitiator;
  isPrimary: boolean;
  requestedAt: string;
  acceptedAt: string | null;
  endedAt: string | null;
  endedReason: string | null;
  /** THIS Runner is the side still owing an answer. Only ever true on a
   *  `requested` row the customer opened — a Runner cannot accept their own offer. */
  awaitingYou: boolean;
}

export type TenureBucket = 'new' | '1-6 months' | '6+ months';
export type DeliveriesBucket = '0-5' | '6-20' | '20+';

/**
 * A zone on the board: DEMAND, never PEOPLE. Built server-side from an
 * allowlist, bucketed, and suppressed entirely below k=5 prospects. Nothing
 * here identifies a household and nothing in this type should ever grow to.
 */
export interface BoardZone {
  zone: string;
  /** How many households are looking — never which. Always >= 5. */
  openings: number;
  typicalWindow: string | null;
  languages: string[];
  tenure: TenureBucket;
  deliveries: DeliveriesBucket;
}

export interface Board {
  zones: BoardZone[];
  /** Zones withheld for being under the threshold — a COUNT, never names. */
  suppressedZoneCount: number;
  /** The zones this Runner actually offers windows in. Empty = no schedule yet. */
  zonesSearched: string[];
}

export type InboxSort = 'unread_first' | 'alerts_first' | 'date_desc' | 'date_asc';
export type InboxCategory = 'runs' | 'pay' | 'insurance' | 'account';
export const INBOX_CATEGORIES: InboxCategory[] = ['runs', 'pay', 'insurance', 'account'];

export interface InboxMessage {
  id: string;
  triggerKey: string;
  category: InboxCategory;
  urgent: boolean;
  alert: boolean;
  title: string | null;
  body: string | null;
  payload: ({ screen?: string } & Record<string, unknown>) | null;
  isRead: boolean;
  createdAt: string;
}

export interface InboxList {
  messages: InboxMessage[];
  total: number;
  skip: number;
  take: number;
}

export interface InboxCounts {
  unreadTotal: number;
  alertUnread: number;
  total: number;
  categoryTotals: Record<InboxCategory, { total: number; unread: number }>;
}

export interface InboxQuery {
  sort?: InboxSort;
  search?: string;
  exact?: boolean;
  category?: InboxCategory;
  unread?: boolean;
  alert?: boolean;
  skip?: number;
  take?: number;
}

function inboxQueryString(q: InboxQuery): string {
  const p = new URLSearchParams();
  if (q.sort) p.set('sort', q.sort);
  if (q.search) p.set('search', q.search);
  if (q.search && q.exact) p.set('exact', 'true');
  if (q.category) p.set('category', q.category);
  if (q.unread) p.set('unread', 'true');
  if (q.alert) p.set('alert', 'true');
  if (q.skip !== undefined) p.set('skip', String(q.skip));
  if (q.take !== undefined) p.set('take', String(q.take));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export interface RelationshipReportInput { reportId: string; reason: 'harassment' | 'safety' | 'delivery' | 'other'; details: string; evidenceKeys: string[]; keepBlocked: boolean }
export interface ReportUpload { key: string; uploadUrl: string; maxBytes: number; uploadHeaders: Record<string, string> }
export const runnerApi = {
  submitRelationshipReport: (matchId: string, body: RelationshipReportInput) => request<{ reportId: string; received: boolean }>('POST', `/runner/me/matches/${encodeURIComponent(matchId)}/reports`, body),
  reportUpload: (matchId: string, body: { reportId: string; contentType: string }) => request<ReportUpload>('POST', `/runner/me/matches/${encodeURIComponent(matchId)}/report-upload`, body),
  me: () => request<Me>('GET', '/runner/me'),
  deleteMe: () => request<{ deleted: boolean; purgeAt: string }>('DELETE', '/runner/me', { acknowledge: 'DELETE-MY-RUNNER-ACCOUNT' }),
  restoreMe: () => request<{ restored: boolean }>('POST', '/runner/me/restore', { acknowledge: 'RESTORE-MY-RUNNER-ACCOUNT' }),
  recoveryByToken: (token: string) => request<{ recovery: { purgeAt: string; canRestore: boolean } | null; restored: boolean }>('POST', '/runner/account/recovery', { token }, true),
  restoreByToken: (token: string) => request<{ restored: boolean }>('POST', '/runner/account/restore', { token, acknowledge: 'RESTORE-MY-RUNNER-ACCOUNT' }, true),

  // Runner inbox (Rod, 2026-10-02): the envelope counts every unread message,
  // the bell counts unread alerts.
  messages: (q: InboxQuery = {}) => request<InboxList>('GET', `/runner/me/messages${inboxQueryString(q)}`),
  messageCounts: () => request<InboxCounts>('GET', '/runner/me/messages/counts'),
  message: (id: string) => request<{ message: InboxMessage }>('GET', `/runner/me/messages/${encodeURIComponent(id)}`),
  setMessageRead: (id: string, isRead: boolean) =>
    request<{ message: InboxMessage }>('PATCH', `/runner/me/messages/${encodeURIComponent(id)}`, { isRead }),
  readAllMessages: () => request<{ updated: number }>('POST', '/runner/me/messages/read-all'),
  bulkMessages: (ids: string[], action: 'read' | 'unread' | 'delete') =>
    request<{ updated: number }>('POST', '/runner/me/messages/bulk', { ids, action }),
  deleteMessage: (id: string) => request<{ deleted: boolean }>('DELETE', `/runner/me/messages/${encodeURIComponent(id)}`),
  restoreMessage: (id: string) => request<{ restored: boolean }>('POST', `/runner/me/messages/${encodeURIComponent(id)}/restore`),
  setPhone: (phoneE164: string) => request<{ phoneE164: string }>('PUT', '/runner/me/phone', { phoneE164 }),
  setLanguage: (language: 'en' | 'es') =>
    request<{ preferredLanguage: 'en' | 'es'; stored: boolean }>('PUT', '/runner/me/language', { language }),
  myApplication: () => request<{ application: MyApplication }>('GET', '/runner/me/application'),
  updateMyApplication: (body: UpdateMyApplication) =>
    request<{ application: MyApplication; changed: boolean }>('PATCH', '/runner/me/application', body),
  withdrawApplication: () => request<{ application: MyApplication }>('POST', '/runner/me/application/withdraw'),
  /** Self-service handbook acknowledgement (2026-09-11); advances training_ack → verification_call when at that step. */
  acknowledgeHandbook: (body: { handbookVersion: string; kitDisclaimerAcknowledged: boolean }) =>
    request<{ acknowledgedAt: string | null; handbookVersion: string | null; kitDisclaimerAckedAt: string | null; status: ApplicationStatus; advanced: boolean }>('POST', '/runner/me/application/training-ack', body),
  // ─── Capacity limits (Rod 2026-09-10/11) ───────────────────────────────
  myLimits: () => request<MyLimits>('GET', '/runner/me/limits'),
  myRecord: () => request<MyRecord>('GET', '/runner/me/record'),
  requestLimitRaise: (body: { kind: 'deliveries' | 'households' | 'both'; requestedDeliveries?: number; requestedHouseholds?: number; note?: string }) =>
    request<{ request: LimitRequest }>('POST', '/runner/me/limits/request', body),

  myInsurance: () => request<MyInsurance>('GET', '/runner/me/insurance'),
  submitInsurance,
  myVerificationCall: () => request<MyVerificationCall>('GET', '/runner/me/verification-call'),

  // §18 photo + vehicle
  myProfile: () => request<{ profile: RunnerProfileDetails | null }>('GET', '/runner/me/profile'),
  updateProfile: (body: { languages?: string[]; bio?: string | null }) => request<{ profile: RunnerProfileDetails }>('PATCH', '/runner/me/profile', body),
  myPhotos: () => request<MyPhotos>('GET', '/runner/me/photo'),
  uploadPhoto,
  withdrawPhoto: (kind: PhotoKind) => request<{ kind: string; withdrawn: boolean }>('DELETE', `/runner/me/photo?kind=${kind}`),
  updateVehicle: (body: UpdateVehicle) => request<{ vehicleDetails: MyPhotos['vehicleDetails'] }>('PATCH', '/runner/me/vehicle', body),

  // §6 availability — recurring DELIVERY windows
  availability: () => request<{ windows: AvailabilityWindow[] }>('GET', '/runner/me/availability'),
  createAvailability: (body: CreateAvailability) =>
    request<{ window: AvailabilityWindow }>('POST', '/runner/me/availability', body),
  updateAvailability: (id: string, body: UpdateAvailability) =>
    request<{ window: AvailabilityWindow }>('PATCH', `/runner/me/availability/${id}`, body),
  /** Ends the window (sets effectiveTo). The row survives — runs reference it. */
  endAvailability: (id: string) =>
    request<{ window: AvailabilityWindow }>('DELETE', `/runner/me/availability/${id}`),

  // §6 time off — the override on top of recurring availability
  timeOff: () => request<{ timeOff: TimeOff[]; noticeTargetDays: number }>('GET', '/runner/me/time-off'),
  createTimeOff: (body: CreateTimeOff) => request<{ timeOff: TimeOff }>('POST', '/runner/me/time-off', body),
  /** Cancels; the row survives, because a customer may already have been told. */
  coverOffers: () => request<{ offers: CoverOffer[] }>('GET', '/runner/me/cover-offers'),
  acceptCover: (id: string) => request<{ runId: string }>('POST', `/runner/me/cover-offers/${id}/accept`, {}),
  declineCover: (id: string) => request<{ declined: boolean }>('POST', `/runner/me/cover-offers/${id}/decline`, {}),
  timeOffCalendar: (from: string, to: string) =>
    request<TimeOffCalendar>('GET', `/runner/me/time-off/calendar?from=${from}&to=${to}`),
  cancelTimeOff: (id: string) => request<{ timeOff: TimeOff }>('DELETE', `/runner/me/time-off/${id}`),

  // §13 step 9a — pickup runs
  /** Today's lineup. The SERVER decides what "today" is, in the Runner's metro
   *  timezone — the browser's clock is not the authority on which runs exist. */
  runs: (params?: { date?: string; from?: string; to?: string; view?: 'active' | 'today' | 'upcoming'; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams(
      Object.entries(params ?? {}).filter(([, v]) => v !== undefined).map(([key, value]) => [key, String(value)]),
    ).toString();
    return request<RunsPage>('GET', `/runner/me/runs${qs ? `?${qs}` : ''}`);
  },
  history: (filters: HistoryFilters & { offset?: number; limit?: number }) => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([, value]) => value !== undefined && value !== '').map(([key, value]) => [key, String(value)]));
    return request<RunsPage>('GET', `/runner/me/runs/history?${qs}`);
  },
  archiveRun: (id: string, archived: boolean) => request<{ id: string; archived: boolean }>('POST', `/runner/me/runs/${id}/archive`, { archived }),
  blocks: () => request<{ blocks: RelationshipBlock[] }>('GET', '/runner/me/blocks'),
  blockMatch: (id: string, blocked: boolean) => request('POST', `/runner/me/matches/${id}/block`, { blocked }),
  orderAttention: (kind: 'pending' | 'disputes', offset = 0) => request<OrderAttentionPage>('GET', `/runner/me/order-attention?kind=${kind}&offset=${offset}&limit=25`),
  run: (id: string) => request<{ run: PickupRun }>('GET', `/runner/me/runs/${id}`),
  startRun: (id: string) => request<{ run: PickupRun }>('POST', `/runner/me/runs/${id}/start`),
  stopArrived: (runId: string, stopId: string) =>
    request<{ run: PickupRun }>('POST', `/runner/me/runs/${runId}/stops/${stopId}/arrived`),
  stopPickedUp: (runId: string, stopId: string) =>
    request<{ run: PickupRun }>('POST', `/runner/me/runs/${runId}/stops/${stopId}/picked-up`),

  // §14 step 10 — earnings. Statements newest first, plus the week in flight.
  earnings: (params?: { from?: string; to?: string; limit?: number }) => {
    const qs = new URLSearchParams(
      Object.entries(params ?? {}).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]),
    ).toString();
    return request<EarningsResponse>('GET', `/runner/me/earnings${qs ? `?${qs}` : ''}`);
  },

  // §13 — customer relationships. Brokered and two-way: whoever asked cannot
  // also answer, and the API enforces it (409). The UI only shows Accept /
  // Decline on rows where `awaitingYou` is true.
  matches: async (status: MatchStatus | 'all' | 'open' = 'open') => {
    const matches: CustomerMatch[] = [];
    let more = true;
    while (more) {
      const page = await request<{ matches: CustomerMatch[]; hasMore?: boolean }>('GET', `/runner/me/matches?status=${status}&limit=200&offset=${matches.length}`);
      matches.push(...page.matches); more = Boolean(page.hasMore);
      if (more && (!page.matches.length || matches.length >= 100000)) throw new RunnerApiError(i18n.t('customers.loadFailed'), 0);
    }
    return { matches };
  },
  acceptMatch: (id: string) => request<{ match: CustomerMatch | null }>('POST', `/runner/me/matches/${id}/accept`),
  declineMatch: (id: string, reason?: string) =>
    request<{ match: CustomerMatch | null }>('POST', `/runner/me/matches/${id}/decline`, reason ? { reason } : undefined),
  pauseMatch: (id: string) => request<{ match: CustomerMatch | null }>('POST', `/runner/me/matches/${id}/pause`),
  resumeMatch: (id: string) => request<{ match: CustomerMatch | null }>('POST', `/runner/me/matches/${id}/resume`),
  endMatch: (id: string, reason?: string) =>
    request<{ match: CustomerMatch | null }>('POST', `/runner/me/matches/${id}/end`, reason ? { reason } : undefined),

  // §13 — the k-anonymous prospect board. Read-only for a Runner: demand in
  // the zones they serve, never people. There is deliberately no "offer to
  // cover" call here — a Runner cannot pick a household off a list.
  board: () => request<Board>('GET', '/runner/me/board'),

  // §5 stage 7 banking — reads THROUGH to Stripe, never a cached column
  notificationPreferences: () => request<{ preferences: NotificationPreferences }>('GET', '/runner/me/notification-preferences'),
  updateNotificationPreferences: (body: NotificationPreferences & { expectedVersion: number }) => request<{ preferences: NotificationPreferences }>('PATCH', '/runner/me/notification-preferences', { optionalPushEnabled: body.optionalPushEnabled, optionalEmailEnabled: body.optionalEmailEnabled, expectedVersion: body.expectedVersion }),
  banking: () => request<BankingState>('GET', '/runner/me/banking'),
  paymentDashboard: () => request<{ url: string }>('POST', '/runner/me/banking/dashboard', {}),
  /** Returns a SINGLE-USE, short-lived Stripe link. Follow it immediately;
   *  never store or share it. */
  startBanking: (body: { returnUrl: string; refreshUrl: string }) =>
    request<{ onboardingUrl: string; expiresAt: number; payable: boolean }>(
      'POST', '/runner/me/banking/start', body,
    ),
};

// ─── Capacity limits (Rod 2026-09-10/11) ─────────────────────────────────────
export type LimitStage = 'starting' | 'trusted' | 'trusted90';
export interface LimitRequest {
  id: string; kind: 'deliveries' | 'households' | 'both'; requestedDeliveries: number | null; requestedHouseholds: number | null; note: string | null;
  status: 'pending' | 'approved' | 'declined'; decidedAt: string | null; decisionReason: string | null;
  grantedDeliveries: number | null; grantedHouseholds: number | null; grantedExpiresAt: string | null; coolingUntil: string | null; createdAt: string;
}
export interface MyLimits {
  stage: LimitStage;
  /** null deliveries = no ceiling. */
  limits: { deliveries: number | null; households: number };
  source: { deliveries: 'tier' | 'override'; households: 'tier' | 'override' };
  overrideExpiresAt: string | null;
  overrideReason: string | null;
  tiers: Record<LimitStage, { deliveries: number | null; households: number }>;
  usage: { weeklyCapacity: number; activeHouseholds: number; deliveriesCompleted: number };
  pendingRequest: LimitRequest | null;
  lastDecision: LimitRequest | null;
  canRequestAfter: string | null;
}

// ─── Your record (§6c) ───────────────────────────────────────────────────────
/** Same shape the Runner app reads. Every number is the server's; nothing is computed here. */
export interface RecordMark {
  id: string;
  kind: string;
  label: string;
  occurredOn: string;
  orderCount: number;
  noticeHours: number | null;
  detail: string | null;
  /** Still inside the window the thresholds are stated in. */
  inWindow: boolean;
}
export interface MyRecord {
  ready: boolean;
  windowDays: number;
  stage: string | null;
  trustedSince: string | null;
  deliveries30: number;
  /** null, never 0 — no ratings yet is not a score of zero. */
  satisfaction: number | null;
  ratingCount: number;
  marks30: number;
  marks: RecordMark[];
  notMarks: string[];
}

export interface RunsPage { runs: PickupRun[]; total?: number; hasMore?: boolean; offset?: number; calendar?: { timeZone: string; today: string } }

export interface HistoryFilters { search?: string; date?: string; from?: string; to?: string; glpo?: string; customer?: string; status?: 'all' | 'completed' | 'cancelled' | 'archived' }
export interface RelationshipBlock { id: string; matchId: string | null; customerGlpUserId: string; createdAt: string }
export interface OrderAttentionPage { orders: Array<{ orderId: string; glpo: string | null; runId: string | null; runDate: string | null; customerDisplayName: string; varianceStatus: string; reviewStatus: string | null; reportedAt: string | null; reviewedAt: string | null; reportReason: string | null }>; total: number; offset: number; limit: number; hasMore: boolean }

export interface RunnerProfileDetails {
  displayName: string; firstName: string; lastName: string; email: string;
  phoneE164: string | null; bio: string | null; languages: string[];
}

export interface NotificationPreferences { optionalPushEnabled: boolean; optionalEmailEnabled: boolean; version: number }
